import { FC, useEffect, useMemo, useState } from 'react'

import { Link } from 'react-router-dom'

import * as constants from 'app/bond_cover/agency/constants'
import AgencySearchBox from 'app/bond_cover/agency/search_box'
import { StyledReportingSettingContainer } from 'app/bond_cover/agency/styles'
import * as utils from 'app/bond_cover/agency/utils'
import AdminAgencySearchResult from 'app/components/admin/AdminAgencySearchResult'
import {
  Box,
  Button,
  Flex,
  Modal,
} from 'app/components/design-system-components'
import { LaunchRounded } from 'app/components/design-system-components/icons/actions'
import { fontSizes } from 'app/components/design-system-components/styles/variables'
import { Text } from 'app/components/design-system-components/typography'
import theme from 'app/match/applicationReportPDF/assets/theme'
import * as snugNotifier from 'app/services/snugNotifier'
import * as FormComponent from 'app/shared_components/form_component'
import { urlTo } from 'app/sm/helpers'
import {
  covertBoolStringToBool,
  covertBoolToString,
} from 'app/utils/strings/helpers'

const defaultAgencyValues = {
  agencyInfo: {
    agencyName: '',
    agencyGUID: '',
  },
  isDirty: true,
}

type IMultiOfficeFormContainerProps = {
  teams: any
  agency: any
  fetchAgencyProfile: any
  searchAgencies: any
  agencyGUID: any
  agencyProfile: any
  onFinishUpdateAgencyProfile: any
  updateAgencyMultipleOfficeSettingInfo: any
}

const MultiOfficeFormContainer: FC<IMultiOfficeFormContainerProps> = (
  props,
) => {
  const {
    agency,
    searchAgencies,
    agencyGUID,
    agencyProfile,
    onFinishUpdateAgencyProfile,
    updateAgencyMultipleOfficeSettingInfo,
  } = props
  const { attributes } = agencyProfile
  const { multipleoffice: multipleOffice, bundle = {} } = attributes

  const [multiTeamReportingOwner, setMultiTeamReportingOwner] =
    useState<boolean>(
      covertBoolStringToBool(
        multipleOffice?.multiple_office_reporting_owner_enabled,
      ),
    )

  const defaultMultipleOfficeList = utils.parseMultipleOfficeObjectToArray(
    multipleOffice,
    constants.agency_reporting_owner_guid,
    constants.agency_reporting_owner_name,
  )

  const [includeActivityReportFrom, setIncludeActivityReportFrom] = useState<
    any[]
  >(defaultMultipleOfficeList)

  const [includeActivityReportIn, setIncludeActivityReportIn] = useState<any[]>(
    [],
  )

  const [effectTime, setEffectTime] = useState<number>(0)
  const [toastMessage, setToastMessage] = useState<string>('')
  const [showModalWrongPlan, setShowModalWrongPlan] = useState<boolean>(false)
  const [showModalDisableSetting, setShowModalDisableSetting] =
    useState<boolean>(false)

  const excludedAgencies: any[] = useMemo(() => {
    return [
      agency.agencyId || agencyGUID,
      ...(
        includeActivityReportFrom.map((item) => item?.agencyInfo?.agencyGUID) ||
        []
      ).filter((item) => !!item),
    ]
  }, [agency, agencyGUID, includeActivityReportFrom])

  const toggleCheckBox = () => {
    setMultiTeamReportingOwner((prev) => {
      if (!prev) {
        setToastMessage(`Successfully enable group activity reporting`)
      } else {
        setToastMessage(`Successfully disable group activity reporting`)
      }
      return !prev
    })
    setEffectTime(new Date().getTime())
  }

  const disableMutlipleSettings = () => {
    setIncludeActivityReportFrom([])
    toggleCheckBox()
  }

  const onChangeMultipleActivityReportingOwner = (e: any) => {
    if (e.target.checked === true) {
      if (!covertBoolStringToBool(bundle.group_bundle)) {
        setShowModalWrongPlan(true)
      } else {
        toggleCheckBox()
      }
    } else {
      setShowModalDisableSetting(true)
    }
  }

  const onAddNewOne = () => {
    setIncludeActivityReportFrom((prev) => [...prev, defaultAgencyValues])
  }

  const onSearchBoxChange = (idx: number, agency: any) => {
    const oldAgency = includeActivityReportFrom[idx]
    if (!agency || !oldAgency) return
    setIncludeActivityReportFrom((prev) => {
      let newIncludeActivityReportFrom = [...prev]
      newIncludeActivityReportFrom[idx] = {
        agencyInfo: {
          agencyName: agency.value,
          agencyGUID: agency.guidID,
        },
        isDirty:
          oldAgency.agencyInfo?.agencyGUID !== '' &&
          agency.guidID &&
          oldAgency.agencyInfo?.agencyGUID !== agency.guidID
            ? true
            : oldAgency.isDirty,
      }
      return newIncludeActivityReportFrom
    })
  }

  const onRemove = (idx: number) => {
    if (window.confirm('Are you sure?')) {
      setIncludeActivityReportFrom((prev) => {
        return prev.filter((_, i) => i !== idx)
      })

      setToastMessage(`Successfully remove include activity reporting from`)
      setEffectTime(new Date().getTime())
    }
  }

  const onSave = (idx: number) => {
    if (includeActivityReportFrom[idx]?.agencyInfo?.agencyGUID) {
      setIncludeActivityReportFrom((prev) => {
        const _includeActivityReportFrom = [...prev]
        _includeActivityReportFrom[idx] = {
          ..._includeActivityReportFrom[idx],
          isDirty: false,
        }
        return _includeActivityReportFrom
      })

      setToastMessage(`Saved new team`)
      setEffectTime(new Date().getTime())
    }
  }

  const getUpdateMultipleAttributes = (
    toSaveTeams: any[],
    isEnable: boolean,
  ) => {
    return {
      multipleoffice: {
        ...utils.formatBeforeSubmitMultipleOffice(
          toSaveTeams,
          constants.multiple_office_agency_reporting_owner,
        ),
        multiple_office_reporting_owner_enabled: covertBoolToString(isEnable),
      },
    }
  }

  const submit = () => {
    const toSaveIncludeActivityReportFrom = includeActivityReportFrom
      .filter((item) => !item.isDirty && !!item.agencyInfo?.agencyGUID)
      .map((item) => item.agencyInfo)

    const attributesUpdate = getUpdateMultipleAttributes(
      toSaveIncludeActivityReportFrom,
      multiTeamReportingOwner,
    )

    let attributesRemove: any[] = []
    if (
      toSaveIncludeActivityReportFrom.length < defaultMultipleOfficeList.length
    ) {
      const deletedIndex = defaultMultipleOfficeList.length
      attributesRemove = [
        `${constants.agency_reporting_owner_guid}_${deletedIndex}`,
        `${constants.agency_reporting_owner_name}_${deletedIndex}`,
      ]
    }

    updateAgencyMultipleOfficeSettingInfo(agency.agencyId || agencyGUID, {
      multipleoffice: {
        [constants.multiple_office_agency_reporting_owner]:
          toSaveIncludeActivityReportFrom
            .map((item: any) => item.agencyGUID)
            .join(','),
        multiple_office_reporting_owner_enabled: covertBoolToString(
          multiTeamReportingOwner,
        ),
      },
    })
      .then(() => {
        onFinishUpdateAgencyProfile(
          attributesUpdate.multipleoffice,
          attributesRemove,
        )
        snugNotifier.success(toastMessage)
      })
      .catch((error: any) => {
        snugNotifier.error(error)
      })
  }

  useEffect(() => {
    if (effectTime && toastMessage) {
      submit()
      setToastMessage('')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectTime, toastMessage])

  const onRemoveTeamIncluded = (team: any) => {
    const {
      teamProfileMultipleOffice,
      teamProfileMultipleOfficeList,
      foundIndex,
    } = team

    const toSaveIncludeActivityReportFrom = teamProfileMultipleOfficeList
      .filter((_item: any, idx: number) => idx !== foundIndex)
      .map((item: any) => item.agencyInfo)

    const attributesUpdate = getUpdateMultipleAttributes(
      toSaveIncludeActivityReportFrom,
      teamProfileMultipleOffice?.multiple_office_reporting_owner_enabled,
    )

    if (window.confirm('Are you sure?')) {
      updateAgencyMultipleOfficeSettingInfo(team.agencyInfo.agencyGUID, {
        multipleoffice: {
          [constants.multiple_office_agency_reporting_owner]:
            toSaveIncludeActivityReportFrom
              .map((item: any) => item.agencyGUID)
              .join(','),
          multiple_office_reporting_owner_enabled: covertBoolToString(
            teamProfileMultipleOffice?.multiple_office_reporting_owner_enabled,
          ),
        },
      })
        .then(() => {
          setIncludeActivityReportIn((prev) =>
            prev.filter(
              (item) =>
                item.agencyInfo.agencyGUID !== team.agencyInfo.agencyGUID,
            ),
          )
          onFinishUpdateAgencyProfile(attributesUpdate.multipleoffice)
          snugNotifier.success(
            `Successfully remove activity reporting include in`,
          )
        })
        .catch((error: any) => {
          snugNotifier.error(error)
        })
    }
  }

  useEffect(() => {
    if (!props.teams) return

    const teamsChecking = props.teams.filter(
      (team: any) => team.guid !== props.agency.guidID && team.slug,
    )

    if (teamsChecking && teamsChecking.length > 0) {
      Promise.all(
        teamsChecking.map(async (team: any) => {
          await props.fetchAgencyProfile(team.guid, (teamProfile: any) => {
            if (teamProfile?.attributes) {
              const teamProfileAttributes = teamProfile?.attributes
              const teamProfileMultipleOffice =
                teamProfileAttributes?.multipleoffice

              const teamProfileMultipleOfficeList =
                utils.parseMultipleOfficeObjectToArray(
                  teamProfileMultipleOffice,
                  constants.agency_reporting_owner_guid,
                  constants.agency_reporting_owner_name,
                )

              const foundTeamProfileIndex =
                teamProfileMultipleOfficeList.findIndex(
                  (item) =>
                    item?.agencyInfo?.agencyGUID === props.agency.guidID,
                )

              if (foundTeamProfileIndex !== -1) {
                setIncludeActivityReportIn((prev) =>
                  prev.concat({
                    teamProfileMultipleOffice,
                    teamProfileMultipleOfficeList,
                    teamProfile,
                    foundIndex: foundTeamProfileIndex,
                    agencyInfo: {
                      slug: teamProfile.slug,
                      agencyName: team.name,
                      agencyGUID: team.guid,
                    },
                  }),
                )
              }
            }
          })
        }),
      )
        .then(() => {})
        .catch((error) => {
          snugNotifier.error(error)
        })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.teams, props.agency])

  const isAgencyPresent = (agencyInfo: any) => !!agencyInfo.agencyGUID

  const renderMultiTeamReportingOwner = () => {
    return (
      <StyledReportingSettingContainer>
        <Box className="col-first relative">
          <FormComponent.Checkbox
            label={'Activity reporting owner'}
            checked={multiTeamReportingOwner}
            onChange={onChangeMultipleActivityReportingOwner}
            componentClassName="mr20 mt10 width100px"
            id={'activity_reporting_owner'}
            toolTip={undefined}
          />

          {multiTeamReportingOwner && (
            <Box mt={theme.dividedSpace[6] + 'px'}>
              <div>
                <Text
                  fontSize={`${fontSizes[6]}px`}
                  marginBottom={theme.dividedSpace[3] + 'px'}
                >
                  Include activity reporting from:
                </Text>
                {includeActivityReportFrom.map(
                  ({ agencyInfo, isDirty }, idx) => {
                    return (
                      <Flex
                        alignItems="center"
                        className="mb10"
                        key={`reporting-from-${idx}`}
                      >
                        <AgencySearchBox
                          placeholder="Search Agency"
                          value={agencyInfo.agencyName}
                          handleInputChange={onSearchBoxChange.bind(null, idx)}
                          inputChangeCallback={() => {}}
                          handleInputBlur={() => {}}
                          className="border-1px border-gray-500 border-radius-6px pt15 pb15 pl20 pr20 width200px"
                          searchFunction={searchAgencies}
                          SearchResultItem={AdminAgencySearchResult}
                          excludedAgencies={excludedAgencies.join(',')}
                          disabled={!isDirty}
                        />
                        <Flex alignItems="center" className="m0">
                          <Box mx={`${theme.baseSpace}px`}>
                            {isDirty ? (
                              <Button
                                style={{
                                  fontSize: theme.fontSizes.pLarge16,
                                }}
                                variant="linkBlueUnderlinedWithHoverBold"
                                onClick={onSave.bind(null, idx)}
                              >
                                save
                              </Button>
                            ) : (
                              <Button
                                style={{
                                  fontSize: theme.fontSizes.pLarge16,
                                }}
                                variant="linkBlueUnderlinedWithHoverBold"
                                onClick={onRemove.bind(null, idx)}
                              >
                                remove
                              </Button>
                            )}
                          </Box>

                          {isAgencyPresent(agencyInfo) && (
                            <Link
                              to={urlTo('editAgency', agencyInfo)}
                              target="_blank"
                            >
                              <LaunchRounded color={theme.colors.gray500} />
                            </Link>
                          )}
                        </Flex>
                      </Flex>
                    )
                  },
                )}
                <Button
                  mt={
                    theme.dividedSpace[
                      includeActivityReportFrom.length > 0 ? 4 : 6
                    ] + 'px'
                  }
                  sizeVariant="large"
                  variant="linkBlueUnderlinedWithHoverBold"
                  onClick={onAddNewOne}
                >
                  add another
                </Button>
              </div>
            </Box>
          )}
          <Box mt={theme.dividedSpace[6] + 'px'}>
            <hr />
          </Box>
          <Box>
            <Text
              fontSize={`${fontSizes[6]}px`}
              marginBottom={theme.dividedSpace[3] + 'px'}
            >
              Activity reporting included in:
            </Text>
            {includeActivityReportIn.length > 0 ? (
              <Box marginBottom={theme.dividedSpace[5] + 'px'}>
                {includeActivityReportIn.map(({ agencyInfo }, idx) => {
                  return (
                    <Flex
                      alignItems="center"
                      className="mb10"
                      key={`reporting-in-${idx}`}
                    >
                      <Box
                        border={`${theme.borders[1]} ${theme.colors.gray500}`}
                        borderRadius={'6px'}
                        py={theme.dividedSpace[3] + 'px'}
                        px={theme.dividedSpace[4] + 'px'}
                        width={theme.width[30]}
                        backgroundColor={theme.colors.gray200}
                      >
                        {agencyInfo.agencyName}
                      </Box>
                      <Flex alignItems="center">
                        <Box mx={`${theme.baseSpace}px`}>
                          <Button
                            style={{
                              fontSize: theme.fontSizes.pLarge16,
                            }}
                            variant="linkBlueUnderlinedWithHoverBold"
                            onClick={onRemoveTeamIncluded.bind(
                              null,
                              includeActivityReportIn[idx],
                            )}
                          >
                            remove
                          </Button>
                        </Box>

                        {isAgencyPresent(agencyInfo) && (
                          <Link
                            to={urlTo('editAgency', agencyInfo)}
                            target="_blank"
                          >
                            <LaunchRounded color={theme.colors.gray500} />
                          </Link>
                        )}
                      </Flex>
                    </Flex>
                  )
                })}
              </Box>
            ) : (
              <p>No teams</p>
            )}
          </Box>
        </Box>
      </StyledReportingSettingContainer>
    )
  }

  return (
    <Box>
      <h4 className="mb25">Activity Reporting</h4>
      <p>
        Group activity reporting <strong>(Group Bundle only)</strong> provides
        reporting from the included teams in the group reporting owner.
      </p>

      {renderMultiTeamReportingOwner()}

      {showModalWrongPlan && (
        <Modal
          modalHeading="Premier plan only"
          modalBody={
            <Box style={{ lineHeight: theme.lineHeights.encrption }} mt={3}>
              Group activity reporting is only available on the{' '}
              <strong>Group Bundle. </strong>
              Obtain Snug General Manager approval on Zendesk ticket to
              activate.
            </Box>
          }
          primaryLabel="Cancel"
          secondaryLabel="Continue"
          primaryAction={() => setShowModalWrongPlan(false)}
          secondaryAction={() => {
            toggleCheckBox()
            setShowModalWrongPlan(false)
          }}
          toggleModal={() => setShowModalWrongPlan(false)}
          modalFooter={undefined}
        />
      )}

      {showModalDisableSetting && (
        <Modal
          modalHeading="Are you sure?"
          modalBody={
            <Box style={{ lineHeight: theme.lineHeights.encrption }} mt={3}>
              All associated teams will be removed from group activity
              reporting.
            </Box>
          }
          primaryLabel="Cancel"
          secondaryLabel="Continue"
          primaryAction={() => setShowModalDisableSetting(false)}
          secondaryAction={() => {
            disableMutlipleSettings()
            setShowModalDisableSetting(false)
          }}
          toggleModal={() => setShowModalDisableSetting(false)}
          modalFooter={undefined}
        />
      )}
    </Box>
  )
}

export default MultiOfficeFormContainer
